import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/cn';

/**
 * FilterChip用のPopoverContentラッパー
 *
 * @remarks
 * FilterChipコンポーネントで使用するPopoverContentの共通実装。
 * 画面高さに応じた動的な高さ調整とスクロール対応を提供します。
 *
 * ## 特徴
 * - **動的な高さ調整**: Radix UIのCSSカスタムプロパティ `--radix-popover-content-available-height` を使用
 * - **画面端からのパディング**: `collisionPadding={20}` で画面端から20pxのパディングを確保
 * - **Flexboxレイアウト**: `flex flex-col` でコンテンツを可変領域に配置
 * - **スクロール対応**: 子要素を `flex-1 overflow-hidden` でラップしてスクロール可能に
 *
 * @example
 * ```tsx
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <FilterChip type="available" iconName="AppsIcon">
 *       ソフトウェア
 *     </FilterChip>
 *   </PopoverTrigger>
 *   <FilterChipPopoverContent>
 *     <Combobox
 *       type="multiple"
 *       options={options}
 *       value={selectedIds}
 *       onValueChange={setSelectedIds}
 *     />
 *   </FilterChipPopoverContent>
 * </Popover>
 * ```
 */
export const FilterChipPopoverContent = forwardRef<HTMLDivElement, Omit<ComponentPropsWithoutRef<typeof PopoverContent>, 'children'> & {
  children: ReactNode;
}>(({
  children,
  className,
  ...props
}, ref) => {
  return <PopoverContent ref={ref} className={cn('w-popover-lg p-0', 'max-h-(--radix-popover-content-available-height)', 'flex flex-col', className)} collisionPadding={20} {...props}>
      <div className={cn('flex-1 min-h-0 flex flex-col overflow-hidden', '[&_[cmdk-root]]:flex-1 [&_[cmdk-root]]:h-auto [&_[cmdk-root]]:min-h-0', '[&_[cmdk-list]]:max-h-none [&_[cmdk-list]]:flex-1 [&_[cmdk-list]]:min-h-0')}>
        {children}
      </div>
    </PopoverContent>;
});
FilterChipPopoverContent.displayName = 'FilterChipPopoverContent';